import {
  Input,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  InputGroup,
  Text,
  Tbody,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { FormulaEngine } from '@finley-technologies/finley-types';

import { LiabilityAccountConfigurationRows } from './LiabilityAccountConfigurationTable';
import { FormFieldLabel } from 'packages/common-frontend/components/FormField';
import {
  AddIconButton,
  ChevronIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const createLiabilityAccountConfigShell = (): CreditAgreement.Config.Liabilities.Inputs.Config => ({
  accrualDates: { recurringDateConfigs: [], exclusions: [] },
  dataAccessors: [],
  intermediateFields: [
    {
      field: 'accrualDate',
      config: {
        formula: 'accrualDate',
        type: 'formula',
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      },
    },
    {
      field: 'accrualValue',
      config: {
        formula: '0',
        type: 'formula',
        engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      },
    },
  ],
});

export const LiabilityAccountRow = ({
  liabilityAccount,
  wipConfig,
  index,
  basePropertyKey,
  fullPropertyKey,
  columnCount,
}: {
  liabilityAccount: CreditAgreement.DomainEntity.LiabilityAccountProperty;
  wipConfig?: WipConfig;
  index: number;
  basePropertyKey: string;
  fullPropertyKey: string;
  columnCount: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const {
    name,
    section,
    tAccount,
    counterpartySlug,
    description,
    _note_,
    _isWrittenIn_,
    _config_,
  } = liabilityAccount;

  const addLiabilityAccountConfig = (wc: WipConfig): void => {
    const newLiabilityAccount = getIn(wc.formProps.values, fullPropertyKey);
    const existingConfigurations =
      newLiabilityAccount._config_?.inputConfigurations ?? [];
    existingConfigurations.push(createLiabilityAccountConfigShell());
    wc.formProps.setFieldValue(fullPropertyKey, {
      ...newLiabilityAccount,
      _config_: { inputConfigurations: existingConfigurations },
    });
  };

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700">
        <Td>{commonUtils.toTitleCase(tAccount)}</Td>
        <Td>{commonUtils.toTitleCase(counterpartySlug)}</Td>
        <Td>{commonUtils.toSentenceCase(name)}</Td>
        <Td>{section}</Td>
        <Td>{description || '-'}</Td>
        {wipConfig && <Td>{_note_ || '-'}</Td>}
        {wipConfig && <Td>{getBooleanSymbol(_isWrittenIn_)}</Td>}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Td>
            <EditIconButton
              ariaLabel="edit"
              label="Edit"
              onClick={(event) => {
                setIsOpen(true);
                if (event) {
                  event.stopPropagation();
                }
              }}
            />
            <AddIconButton
              ariaLabel="add configuration"
              label="Add Configuration"
              onClick={() => {
                addLiabilityAccountConfig(wipConfig);
              }}
            />
            <DeleteArrayPropertyButton
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              idx={index}
              useConfirmationModal
              saveOnDelete
            />
            <EditPropertyModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              wipConfig={wipConfig}
              basePropertyKey={fullPropertyKey}
            >
              {(localFormProps) => {
                return (
                  <Flex alignItems="flex-end" width="100%">
                    <FormFieldLabel labelWidth="60" label="Value" />
                    <InputGroup width="100%">
                      <Input
                        value={localFormProps.values.value}
                        onChange={(e) => {
                          localFormProps.setFieldValue('value', e.target.value);
                        }}
                        width="sm"
                      />
                    </InputGroup>
                  </Flex>
                );
              }}
            </EditPropertyModal>
          </Td>
        )}
        <Td>
          <ChevronIconButton
            openState={isConfigOpen}
            setOpenState={setIsConfigOpen}
            boxSize="5"
          />
        </Td>
      </Tr>
      {isConfigOpen && (
        <LiabilityAccountConfigurationRows
          _config_={_config_}
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          columnCount={columnCount}
        />
      )}
      {/* TODO: @sachinr4 - Placeholder for QA modal */}
    </>
  );
};

export const LiabilityAccountTable = ({
  liabilityAccounts,
  wipConfig,
  basePropertyKey,
}: {
  liabilityAccounts: CreditAgreement.DomainEntity.LiabilityAccountProperty[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
}) => {
  if (!liabilityAccounts.length) {
    return <Text>There are currently no liability accounts configured.</Text>;
  }

  return (
    <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
      <Thead>
        <Tr>
          <Th>Account</Th>
          <Th>Counterparty</Th>
          <Th>Name</Th>
          <Th>Section</Th>
          <Th>Description</Th>
          <Th>Note</Th>
          <Th>Is Written in</Th>
          {wipConfig && <Th>Action</Th>}
          {wipConfig && <Th width="5%" />}
        </Tr>
      </Thead>
      <Tbody>
        {liabilityAccounts.map((liabilityAccount, index) => {
          const fullPropertyKey = `${basePropertyKey}[${[index]}]`; // liabilityAccounts[idx]
          return (
            <LiabilityAccountRow
              key={fullPropertyKey}
              liabilityAccount={liabilityAccount}
              index={index}
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              fullPropertyKey={fullPropertyKey}
              columnCount={wipConfig ? 9 : 7}
            />
          );
        })}
      </Tbody>
    </Table>
  );
};
